<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Hammerdin Build Guide</h1>
    <p>The Hammerdin is one of the most powerful PvE Paladin builds in Diablo 2: Resurrected. It relies on <strong>Blessed Hammer</strong> to deal high magic damage, which can clear almost all areas efficiently because most monsters do not have magic immunity.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-hammerdin.png">
    </div>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <div class="section">
        <h2>Recommended Gear</h2>
        <Equipments
          class="right"
          :left-weapon="weapon"
          :right-weapon="shield"
          :helm="helm"
          :armor="armor"
          :gloves="gloves"
          :belt="belt"
        />
        <h3>Weapon</h3>
        <ul>
            <li>Heart of the Oak – +3 Skills, High Resistances</li>
            <li>Spirit Runeword – Faster Cast Rate, Skills, Mana Regen</li>
        </ul>
        <h3>Shield</h3>
        <ul>
            <li>Spirit Runeword (Paladin Shield) – Faster Cast Rate, Skills, Resistances</li>
        </ul>
        <h3>Helmet</h3>
        <ul>
            <li>Harlequin Crest (Shako) – +2 Skills, High Life and Mana</li>
        </ul>
        <h3>Armor</h3>
        <ul>
            <li>Enigma – Teleport, High Mobility</li>
        </ul>
        <h3>Belt</h3>
        <ul>
            <li>Arachnid Mesh – Faster Cast Rate, +1 Skills</li>
        </ul>
        <h3>Gloves</h3>
        <ul>
            <li>Magefist – Faster Cast Rate, Mana Regeneration</li>
            <li>Trang-Oul's Claws – Faster Cast Rate, Cold Resistance</li>
        </ul>
        <h3>Rings</h3>
        <ul>
            <li>Stone of Jordan – +1 Skills, Increased Mana</li>
            <li>If Faster Cast Rate (FCR) is below 125%, use a +10% FCR rare or crafted ring.</li>
        </ul>
        <h3>Amulet</h3>
        <ul>
            <li>Mara's Kaleidoscope – +2 Skills, All Resistances</li>
        </ul>
    </div>

    <div class="section">
        <h2>Skill Allocation</h2>
        <div class="m-3 skills-tab right">
          <b-tabs small content-class="m-2" class="tab-text">
            <b-tab title="Defensive">
              <img class="w-100" src="@/assets/builds/skills-hammerdin-1.png">
            </b-tab>
            <b-tab title="Offensive">
              <img class="w-100" src="@/assets/builds/skills-hammerdin-2.png">
            </b-tab>
            <b-tab title="Combat" active>
              <img class="w-100" src="@/assets/builds/skills-hammerdin-3.png">
            </b-tab>
          </b-tabs>
        </div>
        <h3>Core Skills (Max 20 Points Each)</h3>
        <ul>
            <li>Blessed Hammer – Primary damage skill</li>
            <li>Vigor – Major synergy for Blessed Hammer</li>
            <li>Blessed Aim – Passive synergy for Blessed Hammer</li>
            <li>Concentration – Main aura to boost Blessed Hammer damage</li>
        </ul>
        <h3>Support Skills</h3>
        <ul>
            <li>Holy Shield – Increases block chance and defense</li>
            <li>Redemption – Used for life and mana recovery</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Stat Distribution</h2>
        <ul>
            <li><strong>Strength:</strong> Just enough to equip gear</li>
            <li><strong>Dexterity:</strong> Enough to achieve 75% block chance (with Holy Shield)</li>
            <li><strong>Vitality:</strong> All remaining points</li>
            <li><strong>Energy:</strong> No points</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Gameplay Tips</h2>
        <h4>1. Combat Rotation</h4>
        <ul>
            <li>Activate <strong>Concentration</strong> Aura to boost Blessed Hammer damage.</li>
            <li>If you don't have Enigma, use <strong>Vigor</strong> Aura while moving for fast repositioning.</li>
            <li>Cast <strong>Blessed Hammer</strong> to deal AoE magic damage.</li>
        </ul>
        <h4>2. Survivability</h4>
        <ul>
            <li>Use <strong>Holy Shield</strong> to increase block chance and defense.</li>
            <li>Activate <strong>Redemption</strong> between fights to quickly restore life and mana.</li>
        </ul>
    </div>
    
    <p>The Hammerdin is one of the most powerful PvE builds due to its high AoE magic damage, great survivability, and excellent mobility.</p>
  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'Hammerdin',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-hammerdin-weapon',
        'width': 80,
        'title': 'Heart of the Oak Runeword',
        'runes': 'Ko + Vex + Pul + Thul',
        'detail': [
          '+3 To All Skills', 
          '+40% Faster Cast Rate',
          '+75% Damage To Demons',
          '+100 To Attack Rating Against DemonsAdds', 
          '3-14 Cold Damage, 3 sec.Duration',
          '7% Mana Stolen Per Hit',
          '+10 To Dexterity',
          'Replenish Life +20',
          'Increase Maximum Mana 15%',
          'All Resistances +30-40',
          'Level 4 Oak Sage (25 Charges)',
          'Level 14 Raven (60 Charges)'
        ],
      }
    },

    shield () {
      return {
        'img': 'eq-hammerdin-shield',
        'width': 80,
        'title': 'Spirit Runeword',
        'runes': 'Tal + Thul + Ort + Amn',
        'detail': [
          '+2 To All Skills', 
          '+25-35% Faster Cast Rate',
          '+55% Faster Hit Recovery',
          '+250 Defense Vs. Missile', 
          '+22 To Vitality',
          '+89-112 To Mana',
          'Cold Resist +35%',
          'Lightning Resist +35%',
          'Poison Resist +35%',
          '+3-8 Magic Absorb',
          'Attacker Takes Damage of 14',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-shako',
        'width': 75,
        'title': 'Harlequin Crest',
        'detail': [
          '+2 To All Skills', 
          '1-148 To Life (Based On Character Level)',
          '1-148 To Mana (Based On Character Level)',
          'Damage Reduced By 10%', 
          '50% Better Chance of Getting Magic Items',
          '+2 To All Attributes'
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-archon-plate',
        'width': 75,
        'title': 'Enigma Runeword',
        'runes': 'Jah + Ith + Ber',
        'detail': [
          '+2 To All Skills', 
          '+45% Faster Run/Walk',
          '+1 To Teleport',
          '+750-775 Defense', 
          '0-74 To Strength (Based On Character Level)',
          'Increase Maximum Life 5%',
          'Damage Reduced By 8%',
          '+14 Life After Each Kill',
          '15% Damage Taken Goes To Mana',
          '+1-99% Better Chance of Getting Magic Items (Based On Character Level)'
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-magefist',
        'width': 70,
        'title': 'Magefist',
        'detail': [
          'Defense: 24-25', 
          '+20-30% Enhanced Defense',
          '+10 Defense',
          '+1 To Fire Skills', 
          '+20% Faster Cast Rate',
          '+30% Faster Hit Recovery',
          'Regenerate Mana 25%',
          'Adds 1-6 Fire Damage',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-arachnid-mesh',
        'width': 75,
        'title': 'Arachnid Mesh',
        'detail': [
          'Defense: 119-138', 
          '+90-120% Enhanced Defense',
          'Slows Target By 10%',
          '+1 To All Skills', 
          '+20% Faster Cast Rate',
          'Increases Maximum Mana 5%',
          'Level 3 Venom (11 Charges)',
        ],
      }
    },
  }
}
</script>
